import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { tr } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      if (data.status === "already_subscribed") {
        toast.info(tr({ it: "Sei già iscritto alla newsletter.", en: "You are already subscribed." }));
      } else {
        toast.success(tr({ it: "Iscrizione confermata. Grazie.", en: "Subscription confirmed. Thank you." }));
      }
      setEmail("");
    } catch {
      toast.error(tr({ it: "Errore. Riprova più tardi.", en: "Error. Please try again later." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[#22242b] bg-[#070709]"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 pb-16">
        <div className="font-editorial italic text-[#a0a5aa] text-2xl md:text-3xl lg:text-4xl leading-[1.15] max-w-4xl mb-16">
          {tr({
            it: "Progettiamo l'aria che respiri come si progetta un'infrastruttura critica — misurabile, certificata, silenziosa.",
            en: "We engineer the air you breathe the way critical infrastructure is engineered — measurable, certified, silent.",
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <img
              src="/brand/logo-light.png"
              alt="Aéràs Technology"
              className="w-full max-w-[360px] h-auto bg-white rounded-sm p-4"
            />
            <div className="font-mono text-xs tracking-[0.3em] text-[#6b7078] mt-6">
              VIA DEL TIRO A SEGNO 10 — 10064 PINEROLO (TO) / ITALIA
            </div>
          </div>

          <div className="md:col-span-3 space-y-3 font-body text-[#a0a5aa]">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#6b7078] mb-4">
              {tr({ it: "NAVIGA", en: "NAVIGATE" })}
            </div>
            <Link to="/chi-siamo" className="block aero-link">{tr({ it: "Chi Siamo", en: "About" })}</Link>
            <Link to="/servizi" className="block aero-link">{tr({ it: "Servizi", en: "Services" })}</Link>
            <Link to="/prodotti" className="block aero-link">{tr({ it: "Prodotti", en: "Products" })}</Link>
            <Link to="/prova-a-casa" className="block aero-link" data-testid="footer-trial-link">
              {tr({ it: "Prova a Casa", en: "Home Trial" })}
            </Link>
            <Link to="/partner" className="block aero-link">Partner</Link>
            <Link to="/blog" className="block aero-link">Blog</Link>
          </div>

          <div className="md:col-span-4 space-y-3 font-body text-[#a0a5aa]">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#6b7078] mb-4">
              {tr({ it: "CONTATTI", en: "CONTACT" })}
            </div>
            <a href="mailto:info@aerastechnology.it" className="block aero-link" data-testid="footer-email">
              info@aerastechnology.it
            </a>
            <a href="tel:+390121097089" className="block aero-link" data-testid="footer-phone">
              Tel 0121.09.70.89
            </a>
            <a
              href="https://carbonplai.com"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-white aero-link"
              data-testid="footer-carbonplai"
            >
              Partner: Carbonplai <ArrowUpRight size={14} />
            </a>

            <form onSubmit={submit} className="pt-6" data-testid="newsletter-form">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#6b7078] mb-3">
                NEWSLETTER
              </div>
              <div className="flex border border-[#22242b] focus-within:border-[#00E5FF] transition-colors">
                <input
                  data-testid="newsletter-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  placeholder={tr({ it: "email@azienda.it", en: "email@company.com" })}
                  className="flex-1 bg-transparent px-4 py-3 outline-none text-sm text-white placeholder:text-[#6b7078] font-body"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="px-4 bg-[#00E5FF] text-black font-mono text-xs tracking-widest disabled:opacity-50 transition-colors hover:bg-white"
                >
                  {loading ? "..." : "→"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="border-t border-[#22242b]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-mono text-[10px] tracking-[0.2em] text-[#6b7078] uppercase">
          <div>© {new Date().getFullYear()} Aéràs Technology — Pinerolo (TO), Italia</div>
          <div className="flex gap-6">
            <span>Privacy</span>
            <span>Cookie</span>
            <Link to="/admin/login" className="hover:text-[#00E5FF]" data-testid="footer-admin-link">
              {tr({ it: "Area riservata", en: "Admin area" })}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
